import { DATAMAPPER_PREFIX } from '@casa/domain';
import { IMetadataApi } from '@kaoto/kaoto';

import { apiBaseUrl } from '../console/data/client';
import { apiFetch } from '../console/data/http';
import { pickFiles } from './file-picker';
import { activeMapping, subscribeActiveMapping } from './mapping-session';
import { canvasSubject } from './open-on-canvas';

const encodePath = (path: string): string => path.split('/').map(encodeURIComponent).join('/');

/**
 * Kaoto's own editors — the DataMapper above all — read and write through an `IMetadataApi`
 * that a host is expected to supply. The self-hosted web app has no host, so those editors are
 * inert in it. This is that host: the files land in Casa's control plane, which means the
 * stylesheet Kaoto's DataMapper saves is the stylesheet the data plane runs.
 */
export class CasaMetadataApi implements IMetadataApi {
  /** In the browser a schema is chosen from disk, so it has to be stored rather than referenced. */
  readonly shouldSaveSchema = true;
  private managedMapping = false;
  subscribeActiveMapping = subscribeActiveMapping;
  getActiveMappingId = () => activeMapping()?.key;
  isResourceReadOnly = (path: string | undefined) =>
    Boolean(canvasSubject() && (!path || path === canvasSubject()?.flowPath));

  constructor(private readonly baseUrl: string = apiBaseUrl()) {}

  private url(...segments: string[]): string {
    return `${this.baseUrl.replace(/\/+$/, '')}/api/v1/${segments.join('/')}`;
  }

  /** While an endpoint is open, every DataMapper step on its canvas is that endpoint's own. */
  private owned(key: string) {
    const active = activeMapping();
    return Boolean(active && (active.key === key || key.startsWith(DATAMAPPER_PREFIX)));
  }

  async getMetadata<T>(key: string): Promise<T | undefined> {
    const active = activeMapping();
    this.managedMapping = this.owned(key);
    if (this.managedMapping) return active!.session.getMetadata<T>(key);
    const response = await apiFetch(this.url('editor-metadata', encodeURIComponent(key)));
    if (!response.ok) return undefined;

    const body = (await response.json()) as { value: T | null };

    return body.value ?? undefined;
  }

  async setMetadata<T>(key: string, metadata: T): Promise<void> {
    const active = activeMapping();
    if (active && this.owned(key)) return active.session.setMetadata(key, metadata);
    const response = await apiFetch(this.url('editor-metadata', encodeURIComponent(key)), {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(metadata),
    });
    if (!response.ok) throw new Error(`Could not save editor metadata (${response.status}).`);
  }

  async getResourceContent(path: string): Promise<string | undefined> {
    if (this.managedMapping && activeMapping()) return activeMapping()!.session.getResourceContent(path);
    const response = await apiFetch(this.url('resources', encodePath(path)));

    return response.ok ? response.text() : undefined;
  }

  async isResourceExist(path: string): Promise<boolean> {
    if (this.managedMapping && activeMapping()) return activeMapping()!.session.isResourceExist(path);
    const response = await apiFetch(this.url('resources', encodePath(path)), { method: 'HEAD' });

    return response.ok;
  }

  async saveResourceContent(path: string, content: string): Promise<void> {
    if (this.managedMapping && activeMapping()) return activeMapping()!.session.saveResourceContent(path, content);
    const response = await apiFetch(this.url('resources', encodePath(path)), {
      method: 'PUT',
      headers: { 'content-type': 'text/plain' },
      body: content,
    });
    if (!response.ok) throw new Error(`Could not save ${path} (${response.status}).`);
  }

  async deleteResource(path: string): Promise<boolean> {
    if (this.managedMapping && activeMapping()) return activeMapping()!.session.deleteResource(path);
    const response = await apiFetch(this.url('resources', encodePath(path)), { method: 'DELETE' });

    return response.ok;
  }

  /**
   * There is no workspace to pick from in the browser, so the browser's file dialog is the picker.
   * Each chosen file is saved where Kaoto then reads it from (into the open mapping, so it stays
   * with the draft), and Kaoto is given the names.
   */
  askUserForFileSelection: IMetadataApi['askUserForFileSelection'] = async (include, _exclude, options) => {
    const files = await pickFiles(include, Boolean(options?.canPickMany));
    const names = Object.keys(files);
    if (names.length === 0) return undefined;
    for (const name of names) await this.saveResourceContent(name, files[name]);
    return options?.canPickMany ? names : names[0];
  };

  getSuggestions: IMetadataApi['getSuggestions'] = async () => [];

  // the control plane tracks published revisions, not keystrokes on the canvas
  onStepUpdated: IMetadataApi['onStepUpdated'] = async () => {};
}
